import sileo from 'sileo';
import axios from 'source/lib/axios';

/*
 * Board related models
 **/
const Board = new sileo.Model('board', 'boards');
const SimpleBoard = new sileo.Model('board', 'simple-board');
const Card = new sileo.Model('board', 'card-panel');
const BoardSidebarCard = new sileo.Model('board', 'board-sidebar-card-id-search');

const CommentMembersSeen = new sileo.Model('board', 'comment-members-seen');
const CardChannel = new sileo.Model('hqzen', 'card-channel');
const CardComment = new sileo.Model('board', 'card-comment');
const CommentAttachmentAmazonCredentials = new sileo.Model('board', 'comment-attachment-amazon-form-data');
const CardActivityUnseen = new sileo.Model('board', 'card-activity-unseen');

const DisplayCards = new sileo.Model('board', 'main-display-card');
const NavbarBoards = new sileo.Model('board', 'navbar-boards');
const MoveCard = new sileo.Model('board', 'move-card');
const CardAttachment = new sileo.Model('board', 'card-attachment');
const CardAttachmentAmazonCredentials = new sileo.Model('board', 'card-attachment-amazon-form-data');
const CardSubtask = new sileo.Model('board', 'card-subtask');
const UpdateMultipleCardSubtasks = new sileo.Model('board', 'update-multiple-card-subtask');
const ClosedCard = new sileo.Model('board', 'closed-cards');
const CardTodos = new sileo.Model('board', 'card-todos');
const CardChannelUnreadCount = new sileo.Model('board', 'card-channel-unread-count');
const FullUserVerification = new sileo.Model('board', 'full-user-verification');
const CardExistenceCheck = new sileo.Model('board', 'card-existence');
const UpdatePinBoard = new sileo.Model('board', 'update-pin-board');
const BoardQuickFilters = new sileo.Model('board', 'board-quick-filter');
const FeatureWaitlist = new sileo.Model('hqzen', 'feature-waitlist');

// TODO (jace@bposeats.com): Rename this; It might simply be CardTemplate
const TasksCardTemplate = new sileo.Model('hqzen', 'tasks-card-templates');
const DismissedTasksCardTemplate = new sileo.Model('hqzen', 'dismissed-card-templates');

const CardChangeLog = new sileo.Model('board', 'card-change-log');
const Milestone = new sileo.Model('board', 'milestone');

// Command Boards
const Workforces = new sileo.Model('board', 'workforce-display');

const MemberActivityExport = new sileo.Model('board', 'member-activity-export');

const OrgSearch = new sileo.Model('hqzen', 'org-search');

const state = {
    boards: {},
    boardsCount: 0,
    nonBposeatsBoardCount: 0,
    messageThreads: {},
    queriedCard: null,

    customCardsClosed: JSON.parse(localStorage.getItem('custom_cards_closed')) ?? [],
};


const getters = {
    boardsByWorkforce: state => {
        let boards = Object.values(state.boards);
        let workforceBoards = {};

        boards.forEach(board => {
            let { workforce_id } = board;

            if (!workforceBoards[workforce_id]) {
                workforceBoards[workforce_id] = [];
            }

            workforceBoards[workforce_id].push(board);
        });
        return workforceBoards;
    },
};


const mutations = {
    setBoards(state, data) {
        let boards = {};
        if (data.addExistingBoards)
            boards = { ...state.boards };

        for (let board of data.objects) {
            if (!boards[board.pk]) {
                boards[board.pk] = board;
            }
        }

        if (JSON.stringify(state.boards) === JSON.stringify(boards)) {
            return;
        }

        state.boards = boards;
        state.boardsCount = data.total_count;
    },

    setBoardTodoCardsCount(state, data) {
        state.boards[data.boardId].todo_column_card_count = data.count;
    },

    setBoardActionableCardsCount(state, data) {
        if (data.count !== undefined) {
            state.boards[data.boardId].actionable_cards_count = Math.max(0, parseInt(data.count));
        } else {
            let count = state.boards[data.boardId].actionable_cards_count || 0;
            if (data.action === 'add') {
                state.boards[data.boardId].actionable_cards_count = count + 1;
            } else if (data.action === 'subtract') {
                state.boards[data.boardId].actionable_cards_count = Math.max(0, count - 1);
            }
        }
    },

    removeBoard(state, boardId) {
        if (!state.boards[boardId])
            return;

        let boards = { ...state.boards };
        delete boards[boardId];
        state.boards = boards;
    },

    setClearBoards(state) {
        state.data = null;
        state.boards = {};
    },

    updateBoardTitle(state, payload) {
        if (!state.boards[payload.pk])
            return;
        let boards = { ...state.boards };
        boards[payload.pk].title = payload.title;
        state.boards = boards;
    },

    updatePinnedBoard(state, payload) {
        let board = state.boards[payload.pk];
        if (!board)
            return;
        board.is_board_pinned = payload.is_board_pinned;
    },

    setQueriedCard(state, card) {
        state.queriedCard = card;
    },

    clearQueriedCard(state) {
        state.queriedCard = null;
    },
};

const actions = {
    /*******************************************
     *    ---- Board Related Actions ----
     ******************************************/

    getBoard(context, pk) {
        return Board.objects.get(pk);
    },

    getSimpleBoard(context, pk) {
        return SimpleBoard.objects.get(pk);
    },

    fetchBoards(context, args) {
        // NOTE:Added the two lines below for backwards compatibility of fetching boards
        const filters = args?.filters ?? args;
        const commit = 'filters' in (args ?? {}) ? args.commit ?? true : true;

        return NavbarBoards.objects.filter(filters).then(data => {
            data.addExistingBoards = !filters.resetBoards;
            if (commit)
                context.commit('setBoards', data);
            return commit ? data.objects : data;
        }).catch(e => {
            console.error('Error fetching boards', e);
            return commit ? [] : null;
        });
    },

    // Board Export
    generateBoardsViewExportCSV(context, params) {
        const url = window.location.pathname;
        return axios.get(url + '/export/csv', {
            params: params
        });
    },

    downloadBoardsViewExportCSV(context, params) {
        const url = 'board/board-export-download';
        return axios.get(url, { params });
    },

    downloadBoardAnalyticsCSV(context, params) {
        const url = window.location.pathname.replace('/board-analytics', '');
        return axios.get(url + '/analytics/export/csv', {
            params: params
        });
    },

    fetchWorkforceBoards(context, filters) {
        return NavbarBoards.objects.filter(filters);
    },

    async updatePinBoard(context, filters) {
        return await UpdatePinBoard.objects.update(filters);
    },

    getBoardQuickFilterData(context, pk) {
        return BoardQuickFilters.objects.get(pk);
    },

    async userVerify(context, payload) {
        let pk = payload.workforce_id;
        let formData = new FormData();
        for (let key in payload) {
            formData.append(key, payload[key]);
        }

        await FullUserVerification.objects.update({ pk }, formData);
    },

    /************************************************
     *       ---- Card Related Actions ----
     ************************************************/

    fetchCards(context, data) {
        return DisplayCards.objects.filter(data);
    },

    async getColumnCard(_, cardId) {
        const response = await DisplayCards.objects.filter({ pk: cardId });
        return response?.objects[0];
    },

    deleteCard(context, filters) {
        return Card.objects.delete(filters);
    },

    moveCard(context, { data, filters }) {
        let form = new FormData();

        for (let key of Object.keys(data)) {
            form.append(key, data[key]);
        }

        return MoveCard.objects.update(filters, form);
    },

    updateCardComment(context, data) {
        const form = new FormData();
        const object_id = data['pk'];
        delete data['pk'];
        for (const field in data) {
            form.append(field, data[field]);
        }

        return CardComment.objects.update({ 'pk': object_id }, form);
    },

    // This is also not used anywhere and a candidate for deprecation
    fetchCardActivities(context, filters) {
        return CardActivityUnseen.objects.filter(filters);
    },

    fetchCardAttachments(context, filters) {
        return CardAttachment.objects.filter(filters);
    },

    createCardAttachment(context, form) {
        return CardAttachment.objects.create(form);
    },

    deleteCardAttachment(context, filters) {
        return CardAttachment.objects.delete(filters);
    },

    async fetchCardAttachmentAmazonFormData(context, filters) {
        return await CardAttachmentAmazonCredentials.objects.filter(filters).then(amazon => {
            return amazon.amazon;
        });
    },

    async fetchCardSubtasks(context, filters) {
        return await CardSubtask.objects.filter(filters);
    },

    async createCardSubtask(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return CardSubtask.objects.create(form);
    },

    async updateMultipleCardSubtask(context, filters) {
        return await UpdateMultipleCardSubtasks.objects.update(filters);
    },

    fetchClosedCards(_, filters) {
        return ClosedCard.objects.filter(filters);
    },

    // TODO (jace@bposeats.com): rename fetchTasksCardTemplates
    fetchTasksCardTemplates(context, filters) {
        return TasksCardTemplate.objects.filter(filters);
    },

    dismissTaskCardTemplates(context, filters) {
        return DismissedTasksCardTemplate.objects.create(filters);
    },

    fetchCardChangeLogs(context, filters) {
        return CardChangeLog.objects.filter(filters);
    },

    deleteCardComment(_, filters) {
        return CardComment.objects.delete(filters);
    },

    fetchWorkforces(context, filters) {
        return Workforces.objects.filter(filters);
    },

    fetchCardComments(context, filters) {
        return CardComment.objects.filter(filters);
    },

    getCardComment(context, pk) {
        return CardComment.objects.get(pk);
    },

    createCardComment(context, data) {
        let form = new FormData();

        for (let [key, value] of Object.entries(data)) {
            form.append(key, value);
        }

        return CardComment.objects.create(form);
    },

    async fetchCommentAttachmentAmazonFormData(context, content_type) {
        return await CommentAttachmentAmazonCredentials.objects.filter(
            { content_type: content_type }).then(amazon => {
                return amazon.amazon;
            });
    },

    async updateCardTodos(context, filters) {
        return await CardTodos.objects.update(filters);
    },

    async fetchCardChannelUnreadCount(context, filters) {
        return await CardChannelUnreadCount.objects.filter(filters);
    },

    updateCommentMemberSeen(context, data) {
        let form = new FormData();
        for (let [key, value] of Object.entries(data)) {
            if (key !== 'object_id')
                form.append(key, value);
        }
        const query = {
            'object_id': data.object_id
        };
        if (data.type) {
            query['type'] = data.type;
        }
        return CommentMembersSeen.objects.update(query, form);
    },

    updateCardActivitySeen(context, data) {
        let form = new FormData();
        for (let [key, value] of Object.entries(data)) {
            form.append(key, value);
        }
        return CardChangeLog.objects.update({ 'card_id': data.card_id }, form);
    },

    fetchCardChannels(context, filters) {
        return CardChannel.objects.filter(filters);
    },

    checkCardExistence(_, filters) {
        return CardExistenceCheck.objects.filter(filters);
    },

    fetchBoardSidebarCards(context, filters) {
        return BoardSidebarCard.objects.filter(filters);
    },

    fetchMilestones(context, filters) {
        return Milestone.objects.filter(filters);
    },

    // We will have to leave the API for the AI card suggestions here for now
    // since we might need to use it in other instances apart from the main board.
    /**
     * Generic action to handle API calls for fetching auto-generated suggestions
     * for card description, card subtasks, and card tags using AI.
     *
     * @param {Object} context - The Vuex store context.
     * @param {Object} params - Parameters to be sent with the API request.
     * @param {string} params.board_pk - PK of the board (for generating card tags).
     * @param {string} params.stream_routing_key - Stream routing key for streamed card descriptions.
     * @param {string} params.title - Card title for generating suggestions (required).
     * @param {Array.<string>} params.selected_tags - A list of already selected tags.
     * @param {string} url - The API endpoint URL for fetching AI card suggestions (required).
     *
     * @returns {Promise} A Promise that resolves with the AI-generated card suggestions.
     */
    fetchAICardSuggestions(context, { params, url }) {
        if (params['selected_tags']) {
            params['selected_tags'] = params['selected_tags'].join(',');
        }

        return axios.post(url, params);
    },

    // Member Activity Export
    downloadMemberActivityExportCSV(context, params) {
        const url = window.location.pathname;
        return axios.get(url + '/export/member-activity/csv', {
            params: params
        });
    },

    // Member Activity Export Queue
    fetchMemberActivityExports(_, filters) {
        return MemberActivityExport.objects.filter(filters);
    },

    createMemberActivityExport(_, data) {
        const form = new FormData();
        Object.keys(data).forEach(key => {
            if (Array.isArray(data[key])) {
                form.append(key, data[key].join(','));
            } else {
                form.append(key, data[key]);
            }
        });
        return MemberActivityExport.objects.create(form);
    },

    deleteMemberActivityExport(_, filters) {
        return MemberActivityExport.objects.delete(filters);
    },

    downloadMemberActivityExport(_, filters) {
        return MemberActivityExport.objects.get(filters);
    },

    searchOrganizationModels(context, filters) {
        return OrgSearch.objects.filter(filters);
    },

    subscribeToFeatureWaitlist(_, data = {}) {
        const form = new FormData();
        form.append('feature_type', data.feature_type);

        return FeatureWaitlist.objects.create(form);
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
